import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';
import type {
  SalesOpsAgents,
  SalesOpsLeadDetail,
  SalesOpsPlan,
  SalesOpsQueue,
  SalesOpsToday,
} from '../types/salesOperations';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

function requireTenantPrefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return tenantApiPrefix(tid);
}

async function readJson<T>(response: Response, fallbackMessage: string): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallbackMessage);
  }
  return response.json();
}

/**
 * Sales Operations (ROCCO) API client.
 *
 * Every endpoint is server-cached for 24 hours; `refresh` bypasses that and
 * recomputes from the warehouse. Caching lives on the server rather than in
 * the browser so CoPilot and the screens cannot disagree about what "now"
 * means -- see `backend/services/sales_operations/cache.py`.
 */

async function get<T>(
  path: string,
  signal: AbortSignal | undefined,
  message: string,
  refresh = false,
): Promise<T> {
  const sep = path.includes('?') ? '&' : '?';
  const url = `${requireTenantPrefix()}/sales-operations${path}${refresh ? `${sep}refresh=true` : ''}`;
  const response = await fetch(buildApiUrl(url), { headers: getAuthHeaders(), signal });
  return readJson<T>(response, message);
}

export function fetchSalesOpsToday(signal?: AbortSignal, refresh = false): Promise<SalesOpsToday> {
  return get<SalesOpsToday>('/today', signal, 'Failed to load the operating picture', refresh);
}

export function fetchSalesOpsPlan(signal?: AbortSignal, refresh = false): Promise<SalesOpsPlan> {
  return get<SalesOpsPlan>('/plan', signal, 'Failed to load capacity plan', refresh);
}

export function fetchSalesOpsQueue(
  limit = 25, signal?: AbortSignal, refresh = false,
): Promise<SalesOpsQueue> {
  return get<SalesOpsQueue>(`/queue?limit=${limit}`, signal, 'Failed to load the queue', refresh);
}

export function fetchSalesOpsLeadDetail(
  leadId: string,
  signal?: AbortSignal,
): Promise<SalesOpsLeadDetail> {
  return get<SalesOpsLeadDetail>(
    `/queue/${encodeURIComponent(leadId)}`,
    signal,
    'Failed to load lead detail',
  );
}

export function fetchSalesOpsAgents(signal?: AbortSignal, refresh = false): Promise<SalesOpsAgents> {
  return get<SalesOpsAgents>('/agents', signal, 'Failed to load agent performance', refresh);
}
